/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, FC, useCallback, useState } from 'react';
import { useQuery } from '@apollo/client';
import { parseCookies } from 'nookies';
import { useBreakpointListener } from '@zoopla/dom-util';
import {
    consentNotShownInLastTenMins,
    CONSENT_COOKIE
} from '@zoopla/consent-utils';

import {
    styledConsentStyle,
    styledCopyStyle,
    trackNudgeContainerStyle
} from './TrackNudge.styles.css';
import { consentText } from '../Button/Button';
import { useDelay } from './helper/useDelay';
import {
    trackNudgeClick,
    trackNudgeClose,
    trackNudgeOpen
} from './TrackNudge.tracking';
import { TRACKED_PROPERTY } from './queries/trackedProperties.query';
import { SaveMedium } from '@zoopla-eng/zds/icons';
import { ButtonLeft } from '@zoopla-eng/zds/button';
import { ControlledDialog } from '@zoopla-eng/zds/dialog';

import { fontGroups } from '@zoopla-eng/zds/themes';

export type TrackNudgeProps = {
    onButtonClick: () => void;
    isAuthModalShowing?: boolean;
};

export const TrackNudge: FC<React.PropsWithChildren<TrackNudgeProps>> = ({
    onButtonClick,
    isAuthModalShowing
}) => {
    const { error, data } = useQuery(TRACKED_PROPERTY, {
        variables: {
            offset: 0,
            limit: 1
        },
        ssr: false
    });

    const delay = 45;

    const { showAfterDelay } = useDelay(delay);

    const isMobile = useBreakpointListener(768, 'LT');

    const [isShowingTrackNudge, updateIsShowingTrackNudge] = useState(false);

    const handleOpenModal = () => {
        updateIsShowingTrackNudge(true);
        trackNudgeOpen();
    };

    const handleCloseModal = () => {
        updateIsShowingTrackNudge(false);
        trackNudgeClose();
    };

    const hasFulfilledCondition = useCallback(() => {
        if (data && data?.user?.trackedProperties?.total === 0) {
            return showAfterDelay;
        }

        return false;
    }, [data, showAfterDelay]);

    const hasConsentGiven = () => {
        return consentNotShownInLastTenMins(parseCookies()[CONSENT_COOKIE]);
    };

    const handleButtonClick = () => {
        if (typeof onButtonClick === 'function') {
            trackNudgeClick();
            updateIsShowingTrackNudge(false);
            onButtonClick();
        }
    };

    useEffect(() => {
        if (
            !isMobile ||
            error ||
            !data ||
            sessionStorage.getItem(`pdp_nudge_modal`) ||
            !hasConsentGiven() ||
            isShowingTrackNudge
        ) {
            return;
        }

        if (hasFulfilledCondition()) {
            // when fulfileld condition and popups opened or window resized, do nothing
            if (
                isAuthModalShowing ||
                !isMobile ||
                // container of Google One Tap
                document.getElementById('credential_picker_container')
            ) {
                return;
            }

            handleOpenModal();
        }
    }, [hasFulfilledCondition, data, isMobile]);

    return (
        <ControlledDialog
            label="Track nudge consent"
            isOpen={isShowingTrackNudge}
            onClose={handleCloseModal}
        >
            <div
                className={trackNudgeContainerStyle}
                data-testid="track-nudge-content"
            >
                <p className={`${styledCopyStyle} ${fontGroups.whisk}`}>
                    See how the estimate changes over time.
                </p>
                <p
                    className={`${styledConsentStyle} ${fontGroups.knife}`}
                    id="nudge-consent"
                >
                    {consentText}.
                </p>
                <ButtonLeft
                    data-testid="track-nudge-button"
                    category="secondary"
                    onClick={handleButtonClick}
                    aria-describedby="nudge-consent"
                    Icon={SaveMedium}
                >
                    Track it now
                </ButtonLeft>
            </div>
        </ControlledDialog>
    );
};
